import React, { useState, useEffect } from 'react';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useApp } from '../../AppContext';
import SideHustleLayout from '../../components/SideHustleLayout';

const TalentsPassions: React.FC = () => {
  const { currentUser } = useApp();
  const [talents, setTalents] = useState<string[]>([]);
  const [passions, setPassions] = useState<string[]>([]);
  const [newTalent, setNewTalent] = useState('');
  const [newPassion, setNewPassion] = useState('');
  const [saving, setSaving] = useState(false);

  // Edit state for talents
  const [editingTalentIndex, setEditingTalentIndex] = useState<number | null>(null);
  const [editTalentValue, setEditTalentValue] = useState('');

  // Edit state for passions
  const [editingPassionIndex, setEditingPassionIndex] = useState<number | null>(null);
  const [editPassionValue, setEditPassionValue] = useState('');

  // Load data from Firebase
  useEffect(() => {
    const loadData = async () => {
      if (!currentUser) return;

      try {
        const docRef = doc(db, 'sideHustleUsers', currentUser.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setTalents(data.talents?.items || []);
          setPassions(data.passions?.items || []);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    loadData();
  }, [currentUser]);

  // Save data to Firebase
  const saveData = async (updatedTalents: string[], updatedPassions: string[]) => {
    if (!currentUser) return;

    setSaving(true);
    try {
      const docRef = doc(db, 'sideHustleUsers', currentUser.uid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        // Update existing document
        await setDoc(docRef, {
          ...docSnap.data(),
          talents: {
            items: updatedTalents,
            lastUpdated: new Date(),
          },
          passions: {
            items: updatedPassions,
            lastUpdated: new Date(),
          },
          lastUpdated: new Date(),
        });
      } else {
        // Create new document
        await setDoc(docRef, {
          userId: currentUser.uid,
          email: currentUser.email,
          createdAt: new Date(),
          lastUpdated: new Date(),
          talents: {
            items: updatedTalents,
            lastUpdated: new Date(),
          },
          passions: {
            items: updatedPassions,
            lastUpdated: new Date(),
          },
        });
      }
    } catch (error) {
      console.error('Error saving data:', error);
    } finally {
      setSaving(false);
    }
  };

  const addTalent = () => {
    if (newTalent.trim()) {
      const updated = [...talents, newTalent.trim()];
      setTalents(updated);
      setNewTalent('');
      saveData(updated, passions);
    }
  };

  const addPassion = () => {
    if (newPassion.trim()) {
      const updated = [...passions, newPassion.trim()];
      setPassions(updated);
      setNewPassion('');
      saveData(talents, updated);
    }
  };

  const removeTalent = (index: number) => {
    const updated = talents.filter((_, i) => i !== index);
    setTalents(updated);
    saveData(updated, passions);
  };

  const removePassion = (index: number) => {
    const updated = passions.filter((_, i) => i !== index);
    setPassions(updated);
    saveData(talents, updated);
  };

  // Edit talent functions
  const startEditingTalent = (index: number) => {
    setEditingTalentIndex(index);
    setEditTalentValue(talents[index]);
  };

  const cancelEditingTalent = () => {
    setEditingTalentIndex(null);
    setEditTalentValue('');
  };

  const saveTalentEdit = () => {
    if (editingTalentIndex !== null && editTalentValue.trim()) {
      const updated = [...talents];
      updated[editingTalentIndex] = editTalentValue.trim();
      setTalents(updated);
      saveData(updated, passions);
      setEditingTalentIndex(null);
      setEditTalentValue('');
    }
  };

  // Edit passion functions
  const startEditingPassion = (index: number) => {
    setEditingPassionIndex(index);
    setEditPassionValue(passions[index]);
  };

  const cancelEditingPassion = () => {
    setEditingPassionIndex(null);
    setEditPassionValue('');
  };

  const savePassionEdit = () => {
    if (editingPassionIndex !== null && editPassionValue.trim()) {
      const updated = [...passions];
      updated[editingPassionIndex] = editPassionValue.trim();
      setPassions(updated);
      saveData(talents, updated);
      setEditingPassionIndex(null);
      setEditPassionValue('');
    }
  };

  return (
    <SideHustleLayout currentPage="talents">
      <div className="flex flex-col items-center p-6 min-h-[calc(100vh-80px)]">
        <div className="max-w-5xl w-full">
          <h1 className="text-5xl font-bold text-center mb-12">Talents & Passions</h1>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Talents Section */}
            <div className="bg-white p-6 rounded-3xl shadow-lg">
              <h2 className="text-3xl font-bold mb-4">Talents</h2>
              <p className="text-gray-600 mb-6">
                What strengths do you have? What things come naturally to you?
              </p>

              <div className="mb-4">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTalent}
                    onChange={(e) => setNewTalent(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addTalent()}
                    placeholder="Enter a talent"
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <button
                    onClick={addTalent}
                    className="bg-primary text-white px-6 py-3 rounded-xl hover:bg-primary/90 transition-all font-semibold"
                  >
                    Add
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                {talents.map((talent, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-secondary px-4 py-3 rounded-xl"
                  >
                    {editingTalentIndex === index ? (
                      <>
                        <input
                          type="text"
                          value={editTalentValue}
                          onChange={(e) => setEditTalentValue(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && saveTalentEdit()}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          autoFocus
                        />
                        <div className="flex gap-2 ml-2">
                          <button
                            onClick={saveTalentEdit}
                            className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 font-semibold"
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEditingTalent}
                            className="bg-gray-400 text-white px-4 py-2 rounded-lg hover:bg-gray-500 font-semibold"
                          >
                            Cancel
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <span className="text-gray-800">
                          {index + 1}. {talent}
                        </span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => startEditingTalent(index)}
                            className="text-primary hover:text-primary/80 font-semibold"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => removeTalent(index)}
                            className="text-red-600 hover:text-red-800 font-semibold"
                          >
                            Remove
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Passions Section */}
            <div className="bg-white p-6 rounded-3xl shadow-lg">
              <h2 className="text-3xl font-bold mb-4">Passions</h2>
              <p className="text-gray-600 mb-6">
                What things light your heart on fire? What areas do you gravitate towards?
              </p>

              <div className="mb-4">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newPassion}
                    onChange={(e) => setNewPassion(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addPassion()}
                    placeholder="Enter a passion"
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <button
                    onClick={addPassion}
                    className="bg-primary text-white px-6 py-3 rounded-xl hover:bg-primary/90 transition-all font-semibold"
                  >
                    Add
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                {passions.map((passion, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-secondary px-4 py-3 rounded-xl"
                  >
                    {editingPassionIndex === index ? (
                      <>
                        <input
                          type="text"
                          value={editPassionValue}
                          onChange={(e) => setEditPassionValue(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && savePassionEdit()}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          autoFocus
                        />
                        <div className="flex gap-2 ml-2">
                          <button
                            onClick={savePassionEdit}
                            className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 font-semibold"
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEditingPassion}
                            className="bg-gray-400 text-white px-4 py-2 rounded-lg hover:bg-gray-500 font-semibold"
                          >
                            Cancel
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <span className="text-gray-800">
                          {index + 1}. {passion}
                        </span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => startEditingPassion(index)}
                            className="text-primary hover:text-primary/80 font-semibold"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => removePassion(index)}
                            className="text-red-600 hover:text-red-800 font-semibold"
                          >
                            Remove
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {saving && (
            <div className="text-center mt-6 text-primary font-semibold">
              Saving...
            </div>
          )}
        </div>
      </div>
    </SideHustleLayout>
  );
};

export default TalentsPassions;
